"use client";
import { RefObject, useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { magneticHover } from "./magneticHover";
import "lenis/dist/lenis.css";

export default function usePortfolioMotion(
  root: RefObject<HTMLDivElement>,
  enabled = true,
  animateHero = true
) {
  useLayoutEffect(() => {
    if (!enabled) return;
    gsap.registerPlugin(ScrollTrigger);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let disposed = false;
    let lenis: { raf: (time: number) => void; destroy: () => void } | undefined;
    const tick = (time: number) => lenis?.raf(time * 1000);
    if (!reduced.matches) {
      import("lenis").then(({ default: Lenis }) => {
        if (disposed) return;
        const instance = new Lenis({
          duration: 1.05,
          smoothWheel: true,
          anchors: { offset: -30 },
        });
        instance.on("scroll", ScrollTrigger.update);
        lenis = instance;
      });
      gsap.ticker.add(tick);
    }
    const ctx = gsap.context(() => {
      gsap.to(".scroll-progress", {
        scaleX: 1,
        ease: "none",
        scrollTrigger: { start: 0, end: "max", scrub: true },
      });
      if (reduced.matches) return;
      if (animateHero)
        gsap.from(".hero-enter", {
          y: 85,
          duration: 1.1,
          stagger: 0.1,
          ease: "power4.out",
          clearProps: "transform",
        });
      gsap.utils.toArray<HTMLElement>(".reveal").forEach((el) =>
        gsap.fromTo(
          el,
          { y: 90 },
          {
            y: 0,
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: "top 96%",
              end: "top 64%",
              scrub: 0.55,
            },
          }
        )
      );
      gsap.utils.toArray<HTMLElement>(".depth-media").forEach((el) =>
        gsap.fromTo(
          el,
          { yPercent: -7, scale: 1.08 },
          {
            yPercent: 7,
            scale: 1.02,
            ease: "none",
            scrollTrigger: {
              trigger: el.parentElement,
              start: "top bottom",
              end: "bottom top",
              scrub: 0.65,
            },
          }
        )
      );
      if (root.current?.querySelector(".hero-watermark"))
        gsap.to(".hero-watermark", {
          xPercent: -20,
          ease: "none",
          scrollTrigger: {
            trigger: ".hero",
            start: "top top",
            end: "bottom top",
            scrub: 0.7,
          },
        });
      if (root.current?.querySelector(".contact-headline"))
        gsap.fromTo(
          ".contact-headline",
          { x: -55 },
          {
            x: 0,
            ease: "none",
            scrollTrigger: {
              trigger: ".contact",
              start: "top bottom",
              end: "top 35%",
              scrub: 0.6,
            },
          }
        );
    }, root);
    // Nested hover transforms remain independent from scroll transforms.
    const buttons = root.current?.querySelectorAll<HTMLElement>(
      ".primary-link,.header-contact,.all-work,.magnetic,.nav-links a"
    );
    const cleanups: (() => void)[] = [];
    if (!reduced.matches && window.matchMedia("(pointer:fine)").matches)
      buttons?.forEach((button) => {
        const visual =
          button.querySelector<HTMLElement>(".magnetic-content") ?? button;
        cleanups.push(magneticHover(button, visual, 0.25, 18, 10));
      });
    return () => {
      disposed = true;
      cleanups.forEach((fn) => fn());
      ctx.revert();
      gsap.ticker.remove(tick);
      lenis?.destroy();
    };
  }, [root, enabled, animateHero]);
}
